import type { ProductBrowseRequest, ProductBrowseStatusCounts } from "../../services/ProductsService";

/** Grid-filter options and helpers for the logged-out feed (see FeedFilters.tsx). */

export type FeedSort = "best" | "updated" | "discount" | "price_low" | "price_high";
export type FeedStatus = "all" | "new" | "price_drop" | "lowest";

export const FEED_SORT_REQUEST: Record<FeedSort, Pick<ProductBrowseRequest, "sortBy" | "sortDirection">> = {
  // Percent off x dollars saved with a category mix (server sortBy "bestDeal").
  best: { sortBy: "bestDeal", sortDirection: "desc" },
  updated: { sortBy: "lastUpdated", sortDirection: "desc" },
  discount: { sortBy: "discountPercentage", sortDirection: "desc" },
  price_low: { sortBy: "storePrice", sortDirection: "asc" },
  price_high: { sortBy: "storePrice", sortDirection: "desc" },
};

export const FEED_SORT_LABELS: Record<FeedSort, string> = {
  best: "Best deal",
  updated: "Newest",
  discount: "Biggest discount",
  price_low: "Price: low to high",
  price_high: "Price: high to low",
};

export const STATUS_LABELS: Record<FeedStatus, string> = {
  all: "All deals",
  new: "New today",
  price_drop: "Price drops",
  lowest: "Lowest price",
};

export const DISCOUNT_OPTIONS = [30, 50, 70] as const;
export const PRICE_CAPS = [10, 25, 50] as const;

export interface FeedFilterValues {
  sort: FeedSort;
  category: string;
  status: FeedStatus;
  /** "" = any */
  minDiscount: string;
  minPrice: string;
  maxPrice: string;
}

export interface FeedFilterHandlers {
  onSort: (value: FeedSort) => void;
  onCategory: (value: string) => void;
  onStatus: (value: FeedStatus) => void;
  onDiscount: (value: string) => void;
  onPrice: (min: string, max: string) => void;
}

export function isFeedSort(value: string | null): value is FeedSort {
  return value === "best" || value === "updated" || value === "discount" || value === "price_low" || value === "price_high";
}

export function isFeedStatus(value: string | null): value is FeedStatus {
  return value === "all" || value === "new" || value === "price_drop" || value === "lowest";
}

/** "$" amounts typed by shoppers: digits and one dot, nothing negative. */
export function cleanPrice(value: string | null): string {
  if (!value) return "";
  const cleaned = value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1");
  return cleaned && Number.isFinite(Number(cleaned)) ? cleaned : "";
}

export function priceLabel(min: string, max: string): string {
  if (min && max) return `$${min}–$${max}`;
  if (max) return `Under $${max}`;
  return `$${min}+`;
}

export function statusCountFor(status: FeedStatus, counts?: ProductBrowseStatusCounts): number | undefined {
  if (!counts) return undefined;
  if (status === "price_drop") return counts.priceDrop;
  return counts[status];
}

/** Status filters only earn a section when the feed actually has new/dropped/lowest items. */
export function hasStatusSignal(counts?: ProductBrowseStatusCounts): boolean {
  return Boolean(counts && (counts.new || counts.priceDrop || counts.lowest));
}

export function activeFilterCount(values: FeedFilterValues): number {
  return [values.category, values.status !== "all", values.minDiscount, values.minPrice || values.maxPrice].filter(Boolean).length;
}
